import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getAllBids,
  getConnectionDetails,
  getDocumentHistory,
  getInterestedListing,
  getLedgerData,
  getMainScreenConnectionDetails,
  getSearchListing,
  getSelfCounterPartyListings,
  getSelfListingById,
  getSelfListings,
  getViewDocumentHistory,
  sideBarConsumerList,
  sideBarGeneratorsList,
  sideBarList,
} from "../../Services/BuySell/Listing";

// Helper for consistent error handling
const handleApiError = (err, rejectWithValue) => {
  return rejectWithValue(
    err.response?.data || err.message || "Something went wrong",
  );
};

// ==========================================
// 1. Self Listings & Capacity Thunks
// ==========================================

export const fetchSelfCapacity = createAsyncThunk(
  "listings/fetchListings",
  async ({ user, showLoading = true }, { rejectWithValue }) => {
    if (!user) return rejectWithValue("User ID is required");

    try {
      let data = await getSelfListings(user);
      return { data: data?.data?.data, showLoading };
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

export const fetchSelfCapacityById = createAsyncThunk(
  "listings/fetchListingById",
  async ({ user, id }, { rejectWithValue }) => {
    if (!user || !id)
      return rejectWithValue("User and Listing ID are required");

    try {
      let data = await getSelfListingById(user, id);
      return { data: data?.data?.data };
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

export const fetchSearchListing = createAsyncThunk(
  "listings/fetchSearchListing",
  async (
    { user, curr, type, state, waitForBackend = false, size },
    { rejectWithValue },
  ) => {
    if (!user) return rejectWithValue("User ID is required for search");

    try {
      if (waitForBackend) {
        await new Promise((resolve) => setTimeout(resolve, 5000));
      }
      let data = await getSearchListing(user, curr, type, state, size);
      return { data: data?.data?.data };
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

// ==========================================
// 2. Sidebar & Navigation Thunks
// ==========================================

export const fetchSideBarConsumerList = createAsyncThunk(
  "listings/fetchSideBarConsumerList",
  async (_, { rejectWithValue }) => {
    try {
      let data = await sideBarConsumerList();
      return { data: data?.data?.data };
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

export const fetchSideBarGeneratorsList = createAsyncThunk(
  "listings/fetchSideBarGeneratorsList",
  async (_, { rejectWithValue }) => {
    try {
      let data = await sideBarGeneratorsList();
      return { data: data?.data?.data };
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

export const fetchSideBarList = createAsyncThunk(
  "listings/fetchSideBarList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await sideBarList();
      return response?.data?.data;
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

export const activeViewId = createAsyncThunk(
  "listings/activeViewId",
  async (id) => id,
);

// ==========================================
// 3. Counter Party & Connections Thunks
// ==========================================

export const fetchCounterPartList = createAsyncThunk(
  "listings/fetchCounterPartList",
  async ({ user, payload, curr, showLoading = true }, { rejectWithValue }) => {
    if (!user) return rejectWithValue("User ID is required");

    try {
      let data = await getSelfCounterPartyListings(user, payload, curr);
      return { data: data?.data?.data, showLoading };
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

export const fetchActiveConnection = createAsyncThunk(
  "connection/activeConnection",
  async (
    { curr, capId, isTermsAccepted, showLoading = true },
    { rejectWithValue },
  ) => {
    if (!capId) return rejectWithValue("Capacity ID is required");

    try {
      let data = await getConnectionDetails(curr, capId, isTermsAccepted);
      return { data: data?.data?.data?.page || data?.data?.data, showLoading };
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

export const fetchMainScreenActiveConnection = createAsyncThunk(
  "connection/mainScreenActiveConnection",
  async ({ capId }, { rejectWithValue }) => {
    if (!capId) return rejectWithValue("Capacity ID is required");

    try {
      let data = await getMainScreenConnectionDetails(capId);
      return { data: data?.data?.data?.page || data?.data?.data };
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

// ==========================================
// 4. Ledger & Bids Thunks
// ==========================================

export const fetchActiveLedger = createAsyncThunk(
  "ledger/fetchCounterPartList",
  async ({ curr, capId }, { rejectWithValue }) => {
    if (!capId) return rejectWithValue("Capacity ID is required");

    try {
      let data = await getLedgerData(curr, capId);
      return { data: data?.data?.data };
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

export const fetchLedgerBids = createAsyncThunk(
  "ledger/fetchLedgerBids",
  async (id, { showLoading = false, rejectWithValue }) => {
    if (!id) return rejectWithValue("ID is required to fetch bids");

    try {
      let data = await getAllBids(id);
      return { data: data?.data?.data, showLoading };
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

export const fetchInterestedListings = createAsyncThunk(
  "ledger/fetchInterestedListings",
  async (id, { rejectWithValue }) => {
    if (!id) return rejectWithValue("ID is required");

    try {
      let data = await getInterestedListing(id);
      return { data: data?.data?.data };
    } catch (err) {
      return handleApiError(err, rejectWithValue);
    }
  },
);

// ==========================================
// 5. Document History Thunks
// ==========================================

export const fetchViewDocumentHistory = createAsyncThunk(
  "bsbp/fetchViewDocumentHistory",
  async (connectedId, { rejectWithValue }) => {
    if (!connectedId) return rejectWithValue("Connected ID is required");

    try {
      const response = await getViewDocumentHistory(connectedId);
      return response?.data || response;
    } catch (error) {
      return handleApiError(error, rejectWithValue);
    }
  },
);

export const fetchDocumentHistory = createAsyncThunk(
  "bsbp/fetchDocumentHistory",
  async ({ connectionId, documentType }, { rejectWithValue }) => {
    if (!connectionId) return rejectWithValue("Connection ID is required");

    try {
      const response = await getDocumentHistory(connectionId, documentType);
      return response.data;
    } catch (error) {
      return handleApiError(error, rejectWithValue);
    }
  },
);

// ==========================================
// Slice Definition
// ==========================================

const initialState = {
  // Data States
  refreshTrigger: 0,
  common: null,
  list: [],
  counterList: [],
  activeConnection: [],
  mainScreenActiveConnection: [],
  ledgerBids: [],
  interestedListings: [],
  activeDiscussion: null,
  documentViewHistory: null,
  documentHistory: null,
  searchListing: [],
  fetchSideBarList: null,
  activeMain: null,
  getSelfListingById: null,

  // IDs
  activeListingId: null,
  counterPartyListingId: null,

  // Loading & Error States
  fetchSelfCapacityLoading: false,
  fetchSelfCapacityError: null,
  fetchSelfCapacityById: false, // This seems to be used as a loading flag in original
  fetchLedgerBidsLoading: false,
  fetchLedgerBidsError: null,
  fetchCounterPartListLoading: false,
  fetchCounterPartListError: null,
  fetchInterestedListingsLoading: false,
  fetchInterestedListingsError: null,
  fetchActiveConnectionLoading: false,
  fetchActiveConnectionError: null,
  fetchMainScreenActiveConnectionLoading: false,
  fetchMainScreenActiveConnectionError: null,
  fetchDocumentHistoryLoading: false,
  fetchDocumentHistoryError: null,
  fetchSideBarListLoading: false,
  fetchSideBarListError: null,
  searchListingLoading: false,
  searchListingError: null,
  loading: false, // General loading
  error: null, // General error
};

const CapacitySlice = createSlice({
  name: "listings",
  initialState,
  reducers: {
    triggerSelfListingRefresh: (state) => {
      state.refreshTrigger += 1;
    },
    setActiveListingId: (state, action) => {
      state.activeListingId = action.payload;
    },
    setCounterPartyListingId: (state, action) => {
      state.counterPartyListingId = action.payload;
    },
    emptyCounterPartyList: (state) => {
      state.counterList = [];
      state.mainScreenActiveConnection = [];
    },
    emptySelfList: (state) => {
      state.list = [];
      state.common = [];
    },
    addNewCounterPartyListing: (state, action) => {
      state.counterList.unshift(action.payload);
    },
    addNewCounterPartyBid: (state, action) => {
      state.ledgerBids.unshift(action.payload);
    },
    updateSelfListingData: (state, action) => {
      state.list = action.payload;
    },
    updateSelfCapacityById: (state, action) => {
      state.getSelfListingById = action.payload?.data;
    },
    updateCounterListingData: (state, action) => {
      state.counterList = action.payload;
    },
    updateActiveConnection: (state, action) => {
      state.activeConnection = action.payload;
    },
    updateMainScreenData: (state, action) => {
      state.mainScreenActiveConnection = action.payload;
    },
    updateCounterListingStatus: (state, action) => {
      const { id, status } = action.payload;
      const listing = state.counterList.find((l) => l.id === id);
      if (listing) {
        listing.status = status;
      }
    },
    updateBidStatus: (state, action) => {
      const data = action.payload; // Object { bidId: status }

      Object.entries(data).forEach(([bidId, status]) => {
        const bid = state.ledgerBids.find(
          (b) => String(b.id) === String(bidId),
        );

        if (!bid) return;

        // 1. Update main bid status
        bid.status = status;
        if (bid.consumer) bid.consumer.status = status;

        // 2. Update status in matches (generators/consumers inside matches)
        if (Array.isArray(bid.matches)) {
          bid.matches.forEach((match) => {
            match.status = status;
            match.generators?.forEach((gen) => (gen.status = status));
            match.consumers?.forEach((con) => (con.status = status));
          });
        }
      });
    },
  },
  extraReducers: (builder) => {
    // --- fetchSelfCapacity ---
    builder
      .addCase(fetchSelfCapacity.pending, (state, action) => {
        if (action.meta.arg?.showLoading !== false)
          state.fetchSelfCapacityLoading = true;
        state.fetchSelfCapacityError = null;
      })
      .addCase(fetchSelfCapacity.fulfilled, (state, action) => {
        state.fetchSelfCapacityLoading = false;
        state.common = action.payload?.data;
        state.list = action.payload?.data?.page;
      })
      .addCase(fetchSelfCapacity.rejected, (state, action) => {
        state.fetchSelfCapacityLoading = false;
        state.fetchSelfCapacityError = action.payload;
      });

    // --- fetchSelfCapacityById ---
    builder
      .addCase(fetchSelfCapacityById.pending, (state) => {
        state.fetchSelfCapacityById = true;
      })
      .addCase(fetchSelfCapacityById.fulfilled, (state, action) => {
        state.fetchSelfCapacityById = false;
        state.getSelfListingById = action.payload?.data;
      })
      .addCase(fetchSelfCapacityById.rejected, (state) => {
        state.fetchSelfCapacityById = false;
      });

    // --- fetchInterestedListings ---
    builder
      .addCase(fetchInterestedListings.pending, (state) => {
        state.fetchInterestedListingsLoading = true;
        state.fetchInterestedListingsError = null;
      })
      .addCase(fetchInterestedListings.fulfilled, (state, action) => {
        state.fetchInterestedListingsLoading = false;
        state.interestedListings = action.payload?.data;
      })
      .addCase(fetchInterestedListings.rejected, (state, action) => {
        state.fetchInterestedListingsLoading = false;
        state.fetchInterestedListingsError = action.payload;
      });

    builder
      .addCase(fetchSideBarConsumerList.pending, (state) => {
        state.fetchSideBarListLoading = true;
      })
      .addCase(fetchSideBarConsumerList.fulfilled, (state, action) => {
        state.fetchSideBarListLoading = false;
        state.fetchSideBarConsumerList = action.payload?.data;
      })
      .addCase(fetchSideBarConsumerList.rejected, (state, action) => {
        state.fetchSideBarListLoading = false;
        state.fetchSideBarListError = action.payload;
      });

    builder
      .addCase(fetchSideBarGeneratorsList.pending, (state) => {
        state.fetchSideBarListLoading = true;
      })
      .addCase(fetchSideBarGeneratorsList.fulfilled, (state, action) => {
        state.fetchSideBarListLoading = false;
        state.fetchSideBarGeneratorsList = action.payload?.data;
      })
      .addCase(fetchSideBarGeneratorsList.rejected, (state, action) => {
        state.fetchSideBarListLoading = false;
        state.fetchSideBarListError = action.payload?.data;
      });

    builder
      .addCase(fetchSideBarList.pending, (state) => {
        state.fetchSideBarListLoading = true;
      })
      .addCase(fetchSideBarList.fulfilled, (state, action) => {
        state.fetchSideBarListLoading = false;
        state.fetchSideBarList = action.payload;
      })
      .addCase(fetchSideBarList.rejected, (state, action) => {
        state.fetchSideBarListLoading = false;
        state.fetchSideBarListError = action.payload;
      });

    // --- fetchCounterPartList ---
    builder
      .addCase(fetchCounterPartList.pending, (state, action) => {
        if (action.meta.arg.showLoading !== false)
          state.fetchCounterPartListLoading = true;
        state.fetchCounterPartListError = null;
      })
      .addCase(fetchCounterPartList.fulfilled, (state, action) => {
        state.fetchCounterPartListLoading = false;
        state.counterList =
          action.payload?.data?.generatorList ||
          action.payload?.data?.consumerList;
        state.activeMain =
          action.payload?.data?.generator || action.payload?.data?.consumer;
      })
      .addCase(fetchCounterPartList.rejected, (state, action) => {
        state.fetchCounterPartListLoading = false;
        state.fetchCounterPartListError = action.payload;
      });

    // --- fetchLedgerBids ---
    builder
      .addCase(fetchLedgerBids.pending, (state, action) => {
        if (action.meta.arg?.showLoading !== false)
          state.fetchLedgerBidsLoading = true;
        state.fetchLedgerBidsError = null;
      })
      .addCase(fetchLedgerBids.fulfilled, (state, action) => {
        state.fetchLedgerBidsLoading = false;
        state.ledgerBids = action.payload?.data;
      })
      .addCase(fetchLedgerBids.rejected, (state, action) => {
        state.fetchLedgerBidsLoading = false;
        state.fetchLedgerBidsError = action.payload;
      });

    // --- fetchSearchListing ---
    builder
      .addCase(fetchSearchListing.pending, (state) => {
        state.searchListingLoading = true;
      })
      .addCase(fetchSearchListing.fulfilled, (state, action) => {
        state.searchListingLoading = false;
        state.searchListing = action.payload?.data?.page;
      })
      .addCase(fetchSearchListing.rejected, (state, action) => {
        state.searchListingLoading = false;
        state.searchListingError = action.payload;
      });

    // --- fetchActiveLedger ---
    builder.addCase(fetchActiveLedger.fulfilled, (state, action) => {
      state.activeDiscussion = action.payload?.data;
    });

    // --- fetchDocumentHistory ---
    builder
      .addCase(fetchDocumentHistory.pending, (state) => {
        state.fetchDocumentHistoryLoading = true;
        state.fetchDocumentHistoryError = null;
      })
      .addCase(fetchDocumentHistory.fulfilled, (state, action) => {
        state.fetchDocumentHistoryLoading = false;
        state.documentHistory = action.payload;
      })
      .addCase(fetchDocumentHistory.rejected, (state, action) => {
        state.fetchDocumentHistoryLoading = false;
        state.fetchDocumentHistoryError = action.payload;
      });

    // --- fetchViewDocumentHistory ---
    builder
      .addCase(fetchViewDocumentHistory.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchViewDocumentHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.documentViewHistory = action.payload;
      })
      .addCase(fetchViewDocumentHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // --- fetchActiveConnection ---
    builder
      .addCase(fetchActiveConnection.pending, (state, action) => {
        if (action.meta.arg.showLoading !== false)
          state.fetchActiveConnectionLoading = true;
        state.fetchActiveConnectionError = null;
      })
      .addCase(fetchActiveConnection.fulfilled, (state, action) => {
        state.fetchActiveConnectionLoading = false;
        state.activeConnection = action.payload?.data;
      })
      .addCase(fetchActiveConnection.rejected, (state, action) => {
        state.fetchActiveConnectionLoading = false;
        state.fetchActiveConnectionError = action.payload;
      });

    // --- fetchMainScreenActiveConnection ---
    builder
      .addCase(fetchMainScreenActiveConnection.pending, (state) => {
        state.fetchMainScreenActiveConnectionLoading = true;
        state.fetchMainScreenActiveConnectionError = null;
      })
      .addCase(fetchMainScreenActiveConnection.fulfilled, (state, action) => {
        state.fetchMainScreenActiveConnectionLoading = false;
        state.mainScreenActiveConnection = action.payload?.data;
      })
      .addCase(fetchMainScreenActiveConnection.rejected, (state, action) => {
        state.fetchMainScreenActiveConnectionLoading = false;
        state.fetchMainScreenActiveConnectionError = action.payload;
      });
  },
});

export const {
  triggerSelfListingRefresh,
  setActiveListingId,
  setCounterPartyListingId,
  updateBidStatus,
  addNewCounterPartyListing,
  emptySelfList,
  updateActiveConnection,
  addNewCounterPartyBid,
  updateCounterListingData,
  updateCounterListingStatus,
  updateSelfListingData,
  emptyCounterPartyList,
  updateMainScreenData,
  updateSelfCapacityById,
} = CapacitySlice.actions;

export default CapacitySlice.reducer;
